/* eslint-disable no-console */
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from "firebase/firestore";

const state = {
  // Reward data
  rewards: [],
  rewardCategories: [],
  currentReward: null,
  currentRewardCategory: null,

  // Loading states
  loadingRewards: false,
  loadingRewardCategories: false,
  loadingCurrentReward: false,
  loadingCurrentRewardCategory: false,
  savingReward: false,
  savingRewardCategory: false,

  // Error state
  error: null
};

const actions = {
  /**
   * Load all rewards for the program
   */
  async loadRewards({ commit, getters }) {
    commit("setLoadingRewards", true);
    commit("setRewardsError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const rewardsRef = collection(programRef, "rewards");
      const rewardsQuery = query(rewardsRef, orderBy("created", "desc"));
      const querySnapshot = await getDocs(rewardsQuery);

      const rewards = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        rewards.push({
          id: docSnapshot.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      commit("setRewards", rewards);
      return rewards;
    } catch (error) {
      console.error("Error loading rewards:", error);
      commit("setRewardsError", error.message);
      commit("setRewards", []);
      throw error;
    } finally {
      commit("setLoadingRewards", false);
    }
  },

  /**
   * Load all reward categories for the program
   */
  async loadRewardCategories({ commit, getters }) {
    commit("setLoadingRewardCategories", true);
    commit("setRewardsError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const rewardCategoriesRef = collection(programRef, "rewardCategories");
      const categoriesQuery = query(
        rewardCategoriesRef,
        orderBy("title", "asc")
      );
      const querySnapshot = await getDocs(categoriesQuery);

      const rewardCategories = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        rewardCategories.push({
          id: docSnapshot.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      commit("setRewardCategories", rewardCategories);
      return rewardCategories;
    } catch (error) {
      console.error("Error loading reward categories:", error);
      commit("setRewardsError", error.message);
      commit("setRewardCategories", []);
      throw error;
    } finally {
      commit("setLoadingRewardCategories", false);
      commit("setLoadingTable", false); // For backward compatibility
    }
  },

  /**
   * Get reward by ID
   */
  async getRewardById({ commit, getters }, rewardId) {
    if (!rewardId) {
      console.error("Reward ID is required");
      return null;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const rewardRef = doc(collection(programRef, "rewards"), rewardId);
      const rewardDoc = await getDoc(rewardRef);

      if (rewardDoc.exists()) {
        const data = rewardDoc.data();
        return {
          id: rewardDoc.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Reward with ID ${rewardId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting reward by ID:", error);
      commit("setRewardsError", error.message);
      return null;
    }
  },

  /**
   * Get reward category by ID
   */
  async getRewardCategoryById({ commit, getters }, categoryId) {
    if (!categoryId) {
      console.error("Reward category ID is required");
      return null;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const categoryRef = doc(
        collection(programRef, "rewardCategories"),
        categoryId
      );
      const categoryDoc = await getDoc(categoryRef);

      if (categoryDoc.exists()) {
        const data = categoryDoc.data();
        return {
          id: categoryDoc.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Reward category with ID ${categoryId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting reward category by ID:", error);
      commit("setRewardsError", error.message);
      return null;
    }
  },

  /**
   * Load current reward details
   */
  async loadCurrentReward({ commit, dispatch }, rewardId) {
    if (!rewardId) {
      throw new Error("Reward ID is required");
    }

    commit("setLoadingCurrentReward", true);
    commit("setCurrentReward", null);
    commit("setRewardsError", null);

    try {
      const reward = await dispatch("getRewardById", rewardId);
      if (reward) {
        commit("setCurrentReward", reward);
        return reward;
      } else {
        throw new Error("Reward not found");
      }
    } catch (error) {
      console.error("Error loading current reward:", error);
      commit("setRewardsError", error.message);
      throw error;
    } finally {
      commit("setLoadingCurrentReward", false);
    }
  },

  /**
   * Load current reward category details
   */
  async loadCurrentRewardCategory({ commit, dispatch }, categoryId) {
    if (!categoryId) {
      throw new Error("Reward category ID is required");
    }

    commit("setLoadingCurrentRewardCategory", true);
    commit("setCurrentRewardCategory", null);
    commit("setRewardsError", null);

    try {
      const category = await dispatch("getRewardCategoryById", categoryId);
      if (category) {
        commit("setCurrentRewardCategory", category);
        return category;
      } else {
        throw new Error("Reward category not found");
      }
    } catch (error) {
      console.error("Error loading current reward category:", error);
      commit("setRewardsError", error.message);
      throw error;
    } finally {
      commit("setLoadingCurrentRewardCategory", false);
    }
  },

  /**
   * Create a new reward
   */
  async createReward({ commit, getters, dispatch }, payload) {
    if (!payload || !payload.title) {
      throw new Error("Reward title is required");
    }

    commit("setSavingReward", true);
    commit("setRewardsError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const rewardsRef = collection(programRef, "rewards");

      const titleUppercase = payload.title.toUpperCase();

      // Check for duplicate titles
      const duplicateQuery = query(
        rewardsRef,
        where("titleUppercase", "==", titleUppercase)
      );
      const duplicateSnapshot = await getDocs(duplicateQuery);

      if (!duplicateSnapshot.empty) {
        throw new Error("A reward with this title already exists");
      }

      const reward = {
        ...payload,
        titleUppercase,
        created: serverTimestamp(),
        updated: serverTimestamp(),
        createdBy: getters.user?.id || "system",
        updatedBy: getters.user?.id || "system",
        isActive: payload.isActive !== false,
        stockQuantity: payload.stockQuantity || 0,
        pointsCost: payload.pointsCost || 0
      };

      const newRewardRef = await addDoc(rewardsRef, reward);

      const tempReward = {
        ...reward,
        id: newRewardRef.id,
        created: new Date(),
        updated: new Date()
      };

      commit("createReward", tempReward);
      dispatch("setSnackbar", "Reward created successfully");

      return {
        success: true,
        rewardId: newRewardRef.id,
        reward: tempReward
      };
    } catch (error) {
      console.error("Error creating reward:", error);
      commit("setRewardsError", error.message);
      if (error.message.includes("title already exists")) {
        throw error;
      }
      throw new Error("Error when creating reward");
    } finally {
      commit("setSavingReward", false);
    }
  },

  /**
   * Create a new reward category
   */
  async createRewardCategory({ commit, getters, dispatch }, payload) {
    if (!payload || !payload.title) {
      throw new Error("Reward category title is required");
    }

    commit("setSavingRewardCategory", true);
    commit("setRewardsError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const categoriesRef = collection(programRef, "rewardCategories");

      const titleUppercase = payload.title.toUpperCase();

      // Check for duplicate titles
      const duplicateQuery = query(
        categoriesRef,
        where("titleUppercase", "==", titleUppercase)
      );
      const duplicateSnapshot = await getDocs(duplicateQuery);

      if (!duplicateSnapshot.empty) {
        throw new Error("A reward category with this title already exists");
      }

      const category = {
        ...payload,
        titleUppercase,
        created: serverTimestamp(),
        updated: serverTimestamp(),
        createdBy: getters.user?.id || "system",
        updatedBy: getters.user?.id || "system",
        isActive: payload.isActive !== false
      };

      const newCategoryRef = await addDoc(categoriesRef, category);

      const tempCategory = {
        ...category,
        id: newCategoryRef.id,
        created: new Date(),
        updated: new Date()
      };

      commit("createRewardCategory", tempCategory);
      dispatch("setSnackbar", "Reward category created successfully");

      return {
        success: true,
        categoryId: newCategoryRef.id,
        category: tempCategory
      };
    } catch (error) {
      console.error("Error creating reward category:", error);
      commit("setRewardsError", error.message);
      if (error.message.includes("title already exists")) {
        throw error;
      }
      throw new Error("Error when creating reward category");
    } finally {
      commit("setSavingRewardCategory", false);
    }
  },

  /**
   * Update an existing reward
   */
  async updateReward({ commit, getters, dispatch }, payload) {
    if (!payload || !payload.id) {
      throw new Error("Reward ID is required");
    }

    commit("setSavingReward", true);
    commit("setRewardsError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const rewardRef = doc(collection(programRef, "rewards"), payload.id);

      // Verify reward exists
      const rewardDoc = await getDoc(rewardRef);
      if (!rewardDoc.exists()) {
        throw new Error("Reward not found");
      }

      // Check for duplicate titles (excluding current reward)
      if (payload.title) {
        const titleUppercase = payload.title.toUpperCase();
        const duplicateQuery = query(
          collection(programRef, "rewards"),
          where("titleUppercase", "==", titleUppercase)
        );
        const duplicateSnapshot = await getDocs(duplicateQuery);

        let duplicated = false;
        duplicateSnapshot.forEach(docSnapshot => {
          if (docSnapshot.id !== payload.id) {
            duplicated = true;
          }
        });

        if (duplicated) {
          throw new Error("A reward with this title already exists");
        }
      }

      const updateData = {
        ...payload,
        updated: serverTimestamp(),
        updatedBy: getters.user?.id || "system"
      };

      if (payload.title) {
        updateData.titleUppercase = payload.title.toUpperCase();
      }

      await updateDoc(rewardRef, updateData);

      const updatedReward = {
        ...rewardDoc.data(),
        ...payload,
        updated: new Date(),
        updatedBy: getters.user?.id || "system"
      };

      commit("updateReward", updatedReward);
      dispatch("setSnackbar", "Reward updated successfully");

      return updatedReward;
    } catch (error) {
      console.error("Error updating reward:", error);
      commit("setRewardsError", error.message);
      if (error.message.includes("title already exists")) {
        throw error;
      }
      throw new Error("Error when updating reward");
    } finally {
      commit("setSavingReward", false);
    }
  },

  /**
   * Update an existing reward category
   */
  async updateRewardCategory({ commit, getters, dispatch }, payload) {
    if (!payload || !payload.id) {
      throw new Error("Reward category ID is required");
    }

    commit("setSavingRewardCategory", true);
    commit("setRewardsError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const categoryRef = doc(
        collection(programRef, "rewardCategories"),
        payload.id
      );

      // Verify category exists
      const categoryDoc = await getDoc(categoryRef);
      if (!categoryDoc.exists()) {
        throw new Error("Reward category not found");
      }

      // Check for duplicate titles (excluding current category)
      if (payload.title) {
        const titleUppercase = payload.title.toUpperCase();
        const duplicateQuery = query(
          collection(programRef, "rewardCategories"),
          where("titleUppercase", "==", titleUppercase)
        );
        const duplicateSnapshot = await getDocs(duplicateQuery);

        let duplicated = false;
        duplicateSnapshot.forEach(docSnapshot => {
          if (docSnapshot.id !== payload.id) {
            duplicated = true;
          }
        });

        if (duplicated) {
          throw new Error("A reward category with this title already exists");
        }
      }

      const updateData = {
        ...payload,
        updated: serverTimestamp(),
        updatedBy: getters.user?.id || "system"
      };

      if (payload.title) {
        updateData.titleUppercase = payload.title.toUpperCase();
      }

      await updateDoc(categoryRef, updateData);

      const updatedCategory = {
        ...categoryDoc.data(),
        ...payload,
        updated: new Date(),
        updatedBy: getters.user?.id || "system"
      };

      commit("updateRewardCategory", updatedCategory);
      dispatch("setSnackbar", "Reward category updated successfully");

      return updatedCategory;
    } catch (error) {
      console.error("Error updating reward category:", error);
      commit("setRewardsError", error.message);
      if (error.message.includes("title already exists")) {
        throw error;
      }
      throw new Error("Error when updating reward category");
    } finally {
      commit("setSavingRewardCategory", false);
    }
  },

  /**
   * Delete a reward
   */
  async deleteReward({ commit, getters, dispatch }, rewardId) {
    if (!rewardId) {
      throw new Error("Reward ID is required");
    }

    commit("setRewardsError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const rewardRef = doc(collection(programRef, "rewards"), rewardId);

      await deleteDoc(rewardRef);

      commit("deleteReward", rewardId);
      dispatch("setSnackbar", "Reward deleted successfully");

      return true;
    } catch (error) {
      console.error("Error deleting reward:", error);
      commit("setRewardsError", error.message);
      throw new Error("Error when deleting reward");
    }
  },

  /**
   * Delete a reward category
   */
  async deleteRewardCategory({ commit, getters, dispatch }, categoryId) {
    if (!categoryId) {
      throw new Error("Reward category ID is required");
    }

    commit("setRewardsError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const categoryRef = doc(
        collection(programRef, "rewardCategories"),
        categoryId
      );

      await deleteDoc(categoryRef);

      commit("deleteRewardCategory", categoryId);
      dispatch("setSnackbar", "Reward category deleted successfully");

      return true;
    } catch (error) {
      console.error("Error deleting reward category:", error);
      commit("setRewardsError", error.message);
      throw new Error("Error when deleting reward category");
    }
  },

  /**
   * Load rewards by category
   */
  async loadRewardsByCategory({ commit, getters }, categoryId) {
    if (!categoryId) {
      throw new Error("Category ID is required");
    }

    commit("setLoadingRewards", true);
    commit("setRewardsError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const rewardsRef = collection(programRef, "rewards");
      const categoryQuery = query(
        rewardsRef,
        where("categoryId", "==", categoryId),
        orderBy("title", "asc")
      );
      const querySnapshot = await getDocs(categoryQuery);

      const rewards = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        rewards.push({
          ...data,
          id: docSnapshot.id,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      return rewards;
    } catch (error) {
      console.error("Error loading rewards by category:", error);
      commit("setRewardsError", error.message);
      throw error;
    } finally {
      commit("setLoadingRewards", false);
    }
  },

  /**
   * Load available rewards (active and in stock)
   */
  async loadAvailableRewards({ commit, getters }) {
    commit("setLoadingRewards", true);
    commit("setRewardsError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const rewardsRef = collection(programRef, "rewards");
      const availableQuery = query(
        rewardsRef,
        where("isActive", "==", true),
        orderBy("pointsCost", "asc")
      );
      const querySnapshot = await getDocs(availableQuery);

      const rewards = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        // Only include rewards that are in stock or have unlimited stock
        if (data.stockQuantity > 0 || data.stockQuantity === -1) {
          rewards.push({
            ...data,
            id: docSnapshot.id,
            created: data.created?.toDate() || new Date(),
            updated: data.updated?.toDate() || new Date()
          });
        }
      });

      return rewards;
    } catch (error) {
      console.error("Error loading available rewards:", error);
      commit("setRewardsError", error.message);
      throw error;
    } finally {
      commit("setLoadingRewards", false);
    }
  },

  // State management actions
  clearRewardsError({ commit }) {
    commit("setRewardsError", null);
  },

  clearCurrentReward({ commit }) {
    commit("setCurrentReward", null);
  },

  clearCurrentRewardCategory({ commit }) {
    commit("setCurrentRewardCategory", null);
  }
};

const mutations = {
  setRewards(state, payload) {
    state.rewards = payload;
  },

  setRewardCategories(state, payload) {
    state.rewardCategories = payload;
  },

  setCurrentReward(state, payload) {
    state.currentReward = payload;
  },

  setCurrentRewardCategory(state, payload) {
    state.currentRewardCategory = payload;
  },

  setLoadingRewards(state, payload) {
    state.loadingRewards = Boolean(payload);
  },

  setLoadingRewardCategories(state, payload) {
    state.loadingRewardCategories = Boolean(payload);
  },

  setLoadingCurrentReward(state, payload) {
    state.loadingCurrentReward = Boolean(payload);
  },

  setLoadingCurrentRewardCategory(state, payload) {
    state.loadingCurrentRewardCategory = Boolean(payload);
  },

  setSavingReward(state, payload) {
    state.savingReward = Boolean(payload);
  },

  setSavingRewardCategory(state, payload) {
    state.savingRewardCategory = Boolean(payload);
  },

  setRewardsError(state, payload) {
    state.error = payload;
  },

  createReward(state, payload) {
    state.rewards = [payload, ...state.rewards];
  },

  createRewardCategory(state, payload) {
    state.rewardCategories = [payload, ...state.rewardCategories];
  },

  updateReward(state, payload) {
    state.rewards = state.rewards.map(item => {
      if (item.id === payload.id) {
        return { ...item, ...payload };
      }
      return item;
    });

    // Update current reward if it matches
    if (state.currentReward && state.currentReward.id === payload.id) {
      state.currentReward = { ...state.currentReward, ...payload };
    }
  },

  updateRewardCategory(state, payload) {
    state.rewardCategories = state.rewardCategories.map(item => {
      if (item.id === payload.id) {
        return { ...item, ...payload };
      }
      return item;
    });

    // Update current category if it matches
    if (
      state.currentRewardCategory &&
      state.currentRewardCategory.id === payload.id
    ) {
      state.currentRewardCategory = {
        ...state.currentRewardCategory,
        ...payload
      };
    }
  },

  deleteReward(state, rewardId) {
    state.rewards = state.rewards.filter(item => item.id !== rewardId);

    // Clear current reward if it matches
    if (state.currentReward && state.currentReward.id === rewardId) {
      state.currentReward = null;
    }
  },

  deleteRewardCategory(state, categoryId) {
    state.rewardCategories = state.rewardCategories.filter(
      item => item.id !== categoryId
    );

    // Clear current category if it matches
    if (
      state.currentRewardCategory &&
      state.currentRewardCategory.id === categoryId
    ) {
      state.currentRewardCategory = null;
    }
  },

  // Backward compatibility
  setLoadingTable(state, payload) {
    state.loadingRewardCategories = Boolean(payload);
  }
};

const getters = {
  /**
   * Get all rewards
   */
  rewards(state) {
    return state.rewards;
  },

  /**
   * Get all reward categories
   */
  rewardCategories(state) {
    return state.rewardCategories;
  },

  /**
   * Get current reward
   */
  currentReward(state) {
    return state.currentReward;
  },

  /**
   * Get current reward category
   */
  currentRewardCategory(state) {
    return state.currentRewardCategory;
  },

  /**
   * Get reward by ID
   */
  getRewardById: state => id => {
    return state.rewards.find(reward => reward.id === id) || null;
  },

  /**
   * Get reward category by ID
   */
  getRewardCategoryById: state => id => {
    return state.rewardCategories.find(category => category.id === id) || null;
  },

  /**
   * Get rewards by category
   */
  getRewardsByCategory: state => categoryId => {
    return state.rewards.filter(reward => reward.categoryId === categoryId);
  },

  /**
   * Get active rewards
   */
  activeRewards(state) {
    return state.rewards.filter(reward => reward.isActive !== false);
  },

  /**
   * Get active reward categories
   */
  activeRewardCategories(state) {
    return state.rewardCategories.filter(
      category => category.isActive !== false
    );
  },

  /**
   * Get available rewards (active and in stock)
   */
  availableRewards(state) {
    return state.rewards.filter(
      reward =>
        reward.isActive !== false &&
        (reward.stockQuantity > 0 || reward.stockQuantity === -1)
    );
  },

  /**
   * Get rewards by price range
   */
  getRewardsByPriceRange: state => (minPoints, maxPoints) => {
    return state.rewards.filter(
      reward => reward.pointsCost >= minPoints && reward.pointsCost <= maxPoints
    );
  },

  /**
   * Get loading states
   */
  loadingRewards(state) {
    return state.loadingRewards;
  },

  loadingRewardCategories(state) {
    return state.loadingRewardCategories;
  },

  loadingCurrentReward(state) {
    return state.loadingCurrentReward;
  },

  loadingCurrentRewardCategory(state) {
    return state.loadingCurrentRewardCategory;
  },

  savingReward(state) {
    return state.savingReward;
  },

  savingRewardCategory(state) {
    return state.savingRewardCategory;
  },

  /**
   * Get error state
   */
  rewardsError(state) {
    return state.error;
  },

  /**
   * Check if any reward operations are loading
   */
  rewardsIsLoading(state) {
    return (
      state.loadingRewards ||
      state.loadingRewardCategories ||
      state.loadingCurrentReward ||
      state.loadingCurrentRewardCategory ||
      state.savingReward ||
      state.savingRewardCategory
    );
  },

  /**
   * Get rewards count
   */
  rewardsCount(state) {
    return state.rewards.length;
  },

  /**
   * Get reward categories count
   */
  rewardCategoriesCount(state) {
    return state.rewardCategories.length;
  },

  /**
   * Get active rewards count
   */
  activeRewardsCount(state) {
    return state.rewards.filter(reward => reward.isActive !== false).length;
  },

  /**
   * Get available rewards count
   */
  availableRewardsCount(state) {
    return state.rewards.filter(
      reward =>
        reward.isActive !== false &&
        (reward.stockQuantity > 0 || reward.stockQuantity === -1)
    ).length;
  },

  /**
   * Check if rewards exist
   */
  hasRewards(state) {
    return state.rewards.length > 0;
  },

  /**
   * Check if reward categories exist
   */
  hasRewardCategories(state) {
    return state.rewardCategories.length > 0;
  },

  /**
   * Check if available rewards exist
   */
  hasAvailableRewards(state) {
    return state.rewards.some(
      reward =>
        reward.isActive !== false &&
        (reward.stockQuantity > 0 || reward.stockQuantity === -1)
    );
  },

  /**
   * Get rewards as options for dropdowns
   */
  rewardsOptions(state) {
    return state.rewards.map(reward => ({
      text: `${reward.title} (${reward.pointsCost} points)`,
      value: reward.id
    }));
  },

  /**
   * Get reward categories as options for dropdowns
   */
  rewardCategoriesOptions(state) {
    return state.rewardCategories.map(category => ({
      text: category.title,
      value: category.id
    }));
  },

  /**
   * Get active reward categories as options
   */
  activeRewardCategoriesOptions(state) {
    return state.rewardCategories
      .filter(category => category.isActive !== false)
      .map(category => ({
        text: category.title,
        value: category.id
      }));
  },

  /**
   * Get available rewards as options
   */
  availableRewardsOptions(state) {
    return state.rewards
      .filter(
        reward =>
          reward.isActive !== false &&
          (reward.stockQuantity > 0 || reward.stockQuantity === -1)
      )
      .map(reward => ({
        text: `${reward.title} (${reward.pointsCost} points)`,
        value: reward.id
      }));
  },

  /**
   * Search rewards by title
   */
  searchRewards: state => searchTerm => {
    if (!searchTerm) return state.rewards;

    const term = searchTerm.toLowerCase();
    return state.rewards.filter(
      reward =>
        reward.title.toLowerCase().includes(term) ||
        (reward.description && reward.description.toLowerCase().includes(term))
    );
  },

  /**
   * Search reward categories by title
   */
  searchRewardCategories: state => searchTerm => {
    if (!searchTerm) return state.rewardCategories;

    const term = searchTerm.toLowerCase();
    return state.rewardCategories.filter(
      category =>
        category.title.toLowerCase().includes(term) ||
        (category.description &&
          category.description.toLowerCase().includes(term))
    );
  },

  /**
   * Get reward statistics
   */
  rewardStatistics(state) {
    const totalRewards = state.rewards.length;
    const activeRewards = state.rewards.filter(
      reward => reward.isActive !== false
    ).length;
    const availableRewards = state.rewards.filter(
      reward =>
        reward.isActive !== false &&
        (reward.stockQuantity > 0 || reward.stockQuantity === -1)
    ).length;
    const totalCategories = state.rewardCategories.length;
    const totalPoints = state.rewards.reduce(
      (sum, reward) => sum + (reward.pointsCost || 0),
      0
    );

    return {
      totalRewards,
      activeRewards,
      availableRewards,
      totalCategories,
      totalPoints,
      averagePointsCost:
        totalRewards > 0 ? Math.round(totalPoints / totalRewards) : 0
    };
  },

  /**
   * Get total rewards stock
   */
  totalRewardsStock(state) {
    return state.rewards.reduce((total, reward) => {
      if (reward.stockQuantity === -1) return total; // Unlimited stock
      return total + (reward.stockQuantity || 0);
    }, 0);
  },

  /**
   * Get rewards sorted by price
   */
  rewardsSortedByPrice(state) {
    return [...state.rewards].sort(
      (a, b) => (a.pointsCost || 0) - (b.pointsCost || 0)
    );
  },

  /**
   * Get recent rewards (last 10)
   */
  recentRewards(state) {
    return [...state.rewards]
      .sort((a, b) => new Date(b.created) - new Date(a.created))
      .slice(0, 10);
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
